/**
 * Image Gallery persisted config (BG-1472 Step 1).
 *
 * Stored shape:
 *   images: [{ url, rotation }]  // rotation is 0 | 90 | 180 | 270
 *   galleryMode: 'static' | 'autoplay'
 *   nextImageDuration: integer seconds, min 1
 *   useIcon: boolean  // true = room icon, false = preview image
 *   previewImage: { url, aspectRatio }
 *
 * Legacy rooms stored `images` as URL strings and `autoplay` as "1"/"2".
 * Load always normalizes; save always writes the new shape.
 *
 * Not stored in V1: captions, per-image duration, links, transition settings.
 */

export const GALLERY_MODE = {
  STATIC: 'static',
  AUTOPLAY: 'autoplay',
};

export const MIN_NEXT_IMAGE_DURATION = 1;
export const DEFAULT_NEXT_IMAGE_DURATION = 3;
export const GALLERY_ROTATION_STEP = 90;
export const GALLERY_DEFAULT_ICON_PATH = '/textures/icons/edit_imageGallery.png';
export const GALLERY_ACCEPTED_IMAGE_FILES = 'image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp';

const emptyPreviewImage = () => ({ url: '', aspectRatio: 1 });

export function getGalleryImageUrl(item) {
  if (!item) return '';
  if (typeof item === 'string') return item;
  return item.url || '';
}

export function getGalleryImageRotation(item) {
  const raw = typeof item === 'object' && item ? Number(item.rotation) : 0;
  if (!Number.isFinite(raw)) return 0;
  const snapped = Math.round(raw / GALLERY_ROTATION_STEP) * GALLERY_ROTATION_STEP;
  return ((snapped % 360) + 360) % 360;
}

export function normalizeGalleryImage(item) {
  const url = getGalleryImageUrl(item);
  if (!url) return null;
  return {
    url,
    rotation: getGalleryImageRotation(item),
  };
}

export function normalizeGalleryImages(images) {
  if (!Array.isArray(images)) return [];
  return images.map(normalizeGalleryImage).filter(Boolean);
}

export function getGalleryImageUrls(images) {
  return normalizeGalleryImages(images).map(getGalleryImageUrl);
}

export function rotateGalleryImage(item, direction) {
  const normalized = normalizeGalleryImage(item);
  if (!normalized) return null;
  const step = direction < 0 ? -GALLERY_ROTATION_STEP : GALLERY_ROTATION_STEP;
  return {
    ...normalized,
    rotation: (normalized.rotation + step + 360) % 360,
  };
}

export function reorderGalleryImages(images, fromIndex, toIndex) {
  const list = normalizeGalleryImages(images);
  const from = Number(fromIndex);
  const to = Number(toIndex);
  if (!Number.isInteger(from) || !Number.isInteger(to)) return list;
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

export function normalizeGalleryMode(config) {
  if (config?.galleryMode === GALLERY_MODE.AUTOPLAY || config?.galleryMode === GALLERY_MODE.STATIC) {
    return config.galleryMode;
  }
  if (config?.autoplay === '2' || config?.autoplay === 2 || config?.autoplay === true) {
    return GALLERY_MODE.AUTOPLAY;
  }
  return GALLERY_MODE.STATIC;
}

export function normalizeNextImageDuration(value) {
  const n = parseInt(value, 10);
  if (!Number.isFinite(n)) return DEFAULT_NEXT_IMAGE_DURATION;
  return Math.max(MIN_NEXT_IMAGE_DURATION, n);
}

export function getGallerySlideDurationMs(config) {
  return normalizeNextImageDuration(config?.nextImageDuration) * 1000;
}

export function clampGallerySlideIndex(index, count) {
  const length = Number(count);
  if (!Number.isFinite(length) || length < 1) return 0;
  const n = Number(index);
  if (!Number.isFinite(n)) return 0;
  const i = Math.trunc(n);
  return ((i % length) + length) % length;
}

export function galleryHasImages(images) {
  return normalizeGalleryImages(images).length > 0;
}

export function galleryHasMultipleImages(images) {
  return normalizeGalleryImages(images).length > 1;
}

export function shouldGalleryAutoplay(config, images) {
  return normalizeGalleryMode(config) === GALLERY_MODE.AUTOPLAY && galleryHasMultipleImages(images);
}

export function normalizeGalleryUseIcon(config) {
  if (config?.useIcon !== undefined) return config.useIcon === true;
  // Legacy galleries are always a spatial preview mesh.
  return false;
}

export function normalizePreviewImage(config) {
  const preview = config?.previewImage;
  if (typeof preview === 'string') {
    return { url: preview, aspectRatio: 1 };
  }
  if (preview && typeof preview === 'object') {
    return {
      url: preview.url || '',
      aspectRatio: Number(preview.aspectRatio) > 0 ? Number(preview.aspectRatio) : 1,
    };
  }
  return emptyPreviewImage();
}

/** Cover used in the room when Preview image is selected. */
export function getGalleryCoverUrl(config) {
  const preview = normalizePreviewImage(config);
  if (preview.url) return preview.url;
  return getGalleryImageUrl(config?.images?.[0]);
}

export function getGalleryUploadJobId(file) {
  return file?.upload?.uuid || [file?.name, file?.size, file?.lastModified].filter(Boolean).join('-');
}

export function formatGalleryUploadError(file, errorMessage) {
  const raw = typeof errorMessage === 'string'
    ? errorMessage
    : (errorMessage?.message || errorMessage?.error || '');
  const name = file?.name || '';
  const fallback = typeof $trans === 'function' ? $trans('gallery-upload-failed') : 'Upload failed';
  if (/too big|filesize|max filesize/i.test(raw)) {
    return typeof $trans === 'function' ? $trans('gallery-upload-file-too-big') : raw;
  }
  if (/type|accepted|invalid/i.test(raw) || /\.gif$/i.test(name) || (file?.type || '').includes('gif')) {
    return typeof $trans === 'function' ? $trans('gallery-upload-unsupported-file') : raw;
  }
  if (!raw || /<[a-z][\s\S]*>/i.test(raw)) return fallback;
  return raw;
}

/** Dedicated preview asset only — empty when the first gallery image is the cover. */
export function getGalleryPreviewAssetUrl(config) {
  return normalizePreviewImage(config).url || '';
}

export function persistGalleryConfig(config = {}) {
  const images = normalizeGalleryImages(config.images);
  const galleryMode = normalizeGalleryMode(config);
  return {
    ...config,
    images,
    galleryMode,
    nextImageDuration: normalizeNextImageDuration(config.nextImageDuration),
    useIcon: normalizeGalleryUseIcon(config),
    previewImage: normalizePreviewImage(config),
    // Keep legacy autoplay in sync for the existing editor checkbox until Step 3.
    autoplay: galleryMode === GALLERY_MODE.AUTOPLAY ? '2' : '1',
  };
}
